/**
 * Единый перехват «пропуск Telegram больше не принимают» (work-038).
 *
 * `api/client.ts` — обычный модуль вне React-дерева: до `ToastProvider` он не
 * дотягивается, а готового канала «клиент API → глобальный UI» в проекте нет.
 * Поэтому здесь module-scope подписка: клиент зовёт `notifyAuthFailure()`,
 * `App` подписывается и показывает честный полноэкранный экран.
 *
 * Модуль НИКОГДА не бросает (CANON G3), не зависит от React и НЕ импортирует
 * значений из `api/` — только тип (`import type`), иначе получился бы цикл
 * импорта `client.ts → sessionExpiry.ts → errors.ts` в обе стороны.
 *
 * CANON SP3 это не ослабляет: единственная граница доверия — серверный 401,
 * здесь только UX вокруг уже полученного отказа.
 */
import type { AuthFailureReason } from '../api/errors.js';

/** Зафиксированный отказ: причина + момент первого срабатывания (unix ms). */
export type SessionFailure = { reason: AuthFailureReason; at: number };

/**
 * Окно, в течение которого причину ещё можно уточнить (`'unknown'` → конкретная).
 * Зачем ограничение: на bootstrap уходит несколько запросов разом, и ответы
 * приходят вперемешку. Без окна заголовок экрана менялся бы под уже читающим
 * человеком спустя секунды. 300 мс — разброс внутри одной пачки bootstrap-запросов.
 */
const UPGRADE_WINDOW_MS = 300;

/** Первое срабатывание фиксируется до перезапуска приложения. */
let failure: SessionFailure | null = null;
/** Диспатч в window — ровно один раз за запуск (см. `reportToWatch`). */
let reported = false;
const subscribers = new Set<(f: SessionFailure) => void>();

/**
 * Ре-репорт сбоя в глобальное событие `window 'error'` — так он доезжает до
 * ловушки mini-watch, которая слушает именно window-события (`boot/armWatch.ts`).
 * Образец ре-репорта — `components/ErrorBoundary.tsx:52`.
 *
 * Телеметрию НЕЛЬЗЯ слать через `emitEvent`: он уйдёт тем же `apiFetch`, тоже
 * получит 401 и будет проглочен.
 */
function reportToWatch(reason: AuthFailureReason): void {
  try {
    const err = new Error(`auth-failure: ${reason}`);
    window.dispatchEvent(new ErrorEvent('error', { error: err, message: err.message }));
  } catch {
    // dispatchEvent/ErrorEvent недоступны в окружении — наблюдаемость не
    // критична, экран пользователю уже показан.
  }
}

function emit(f: SessionFailure): void {
  for (const cb of subscribers) {
    try {
      cb(f);
    } catch {
      // Подписчик упал — это не повод ронять остальных (CANON G3).
    }
  }
}

/**
 * Подписка на отказ авторизации. Возвращает функцию отписки.
 *
 * Если отказ УЖЕ случился к моменту подписки — колбэк вызывается сразу.
 * Это обязательно: `InboxScreen` шлёт `POST /boxes` раньше, чем `App`
 * успевает смонтировать эффект подписки, и без немедленной выдачи первый
 * (единственный) отказ был бы потерян навсегда.
 */
export function subscribeAuthFailure(cb: (f: SessionFailure) => void): () => void {
  subscribers.add(cb);
  if (failure !== null) {
    try {
      cb(failure);
    } catch {
      // см. emit()
    }
  }
  return () => {
    subscribers.delete(cb);
  };
}

/**
 * Сообщить о непринятом пропуске. Многократные вызовы безопасны.
 *
 * Правила:
 * - первое срабатывание фиксируется и рассылается подписчикам;
 * - разрешён ОДНОКРАТНЫЙ апгрейд `'unknown'` → конкретная причина и только в
 *   пределах `UPGRADE_WINDOW_MS` от первого срабатывания; позже — no-op;
 * - `window`-событие диспатчится ровно один раз, на первом срабатывании,
 *   с исходной причиной; апгрейд повторно НЕ диспатчит.
 */
export function notifyAuthFailure(reason: AuthFailureReason): void {
  try {
    if (failure === null) {
      failure = { reason, at: Date.now() };
      if (!reported) {
        reported = true;
        reportToWatch(reason);
      }
      emit(failure);
      return;
    }

    // Апгрейд причины — только 'unknown' → конкретная, только внутри окна.
    if (failure.reason !== 'unknown') return;
    if (reason === 'unknown') return;
    if (Date.now() - failure.at >= UPGRADE_WINDOW_MS) return;

    // `at` НЕ обновляем: это момент первого отказа, по нему считается окно.
    failure = { reason, at: failure.at };
    emit(failure);
  } catch {
    // Ни при каких обстоятельствах не бросаем в вызывающий код (CANON G3):
    // клиент API зовёт это перед `throw` своей типизированной ошибки.
  }
}

/**
 * Сброс module-scope состояния. ТОЛЬКО для тестов — в продовом коде отказ
 * живёт до перезапуска приложения. Снимает и подписчиков, чтобы соседние
 * тесты не получали чужие колбэки.
 */
export function __resetAuthFailureForTests(): void {
  failure = null;
  reported = false;
  subscribers.clear();
}

/**
 * Показывать ли нижнюю навигацию. Вынесена сюда чистой функцией ради
 * тестируемости без рендера `App` (харнесса для рендера `App` в репозитории
 * нет: он тянет живой SDK и пять сетевых вызовов).
 *
 * При зафиксированном отказе таб-бар гасится: переключать вкладки бессмысленно,
 * любой запрос всё равно получит 401.
 *
 * Список вкладок ПРИНИМАЕТСЯ аргументом, а не дублируется здесь: единственный
 * источник истины — типизированный `TAB_SCREENS` в `App.tsx`, привязанный к
 * `Screen['kind']`. Своя копия молча расходилась бы с ним при добавлении вкладки.
 */
export function shouldShowTabbar(
  screenKind: string,
  failed: boolean,
  tabScreens: readonly string[],
): boolean {
  return !failed && tabScreens.includes(screenKind);
}
